/* utils/services/IpGeoLocationService.ts ---------------------------------- *
 * Service for IP-based geolocation using ip-api.hiiker.com                    *
 * -------------------------------------------------------------------------- */

const HIIKER_IP_API_BASE = 'https://ip-api.hiiker.com';
const HIIKER_IP_API_KEY = import.meta.env.VITE_HIIKER_IP_API_KEY;

export interface GeoLocation {
  lat: number;
  lng: number;
  city?: string;
  region?: string;
  country?: string;
}

class IpGeoLocationService {
  private static _instance: IpGeoLocationService;
  private cachedLocation: GeoLocation | null = null;
  private fetchPromise: Promise<GeoLocation | null> | null = null;

  static get shared(): IpGeoLocationService {
    if (!IpGeoLocationService._instance) {
      IpGeoLocationService._instance = new IpGeoLocationService();
    }
    return IpGeoLocationService._instance;
  }

  private constructor() {
    if (IpGeoLocationService._instance) {
      throw new Error('Use IpGeoLocationService.shared, not constructor.');
    }
  }

  /**
   * Get user's geolocation based on their IP address.
   * Results are cached for the session to avoid repeated API calls.
   */
  async getLocation(): Promise<GeoLocation | null> {
    if (this.cachedLocation) {
      return this.cachedLocation;
    }

    // Check if window.ip_response is already available (from server-side rendering)
    if (window.ip_response?.latitude && window.ip_response?.longitude) {
      this.cachedLocation = {
        lat: window.ip_response.latitude,
        lng: window.ip_response.longitude,
        city: window.ip_response.city,
        region: window.ip_response.region,
        country: window.ip_response.country_name
      };
      return this.cachedLocation;
    }

    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = this.fetchLocation();
    const result = await this.fetchPromise;
    this.fetchPromise = null;
    return result;
  }

  private async fetchLocation(): Promise<GeoLocation | null> {
    try {
      // First get our public IP via the /raw endpoint
      const ipResponse = await fetch(`${HIIKER_IP_API_BASE}/raw`);
      if (!ipResponse.ok) {
        console.error(`IP API /raw error: ${ipResponse.status}`);
        return null;
      }
      const ip = (await ipResponse.text()).trim();

      // Then look up geolocation for that IP
      const geoResponse = await fetch(
        `${HIIKER_IP_API_BASE}/geolookup/${ip}?api_key=${HIIKER_IP_API_KEY}`
      );
      if (!geoResponse.ok) {
        console.error(`IP API geolookup error: ${geoResponse.status}`);
        return null;
      }

      const raw = await geoResponse.json();
      const entry = Array.isArray(raw) ? raw[0] : raw;
      const countryInfo = entry?.country_info || {};

      const lat = countryInfo['Latitude (average)'];
      const lng = countryInfo['Longitude (average)'];

      if (!lat || !lng) {
        console.error('IP API returned no coordinates');
        return null;
      }

      this.cachedLocation = {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        country: countryInfo['Country']
      };

      return this.cachedLocation;
    } catch (error) {
      console.error('Failed to fetch IP geolocation:', error);
      return null;
    }
  }

  clearCache(): void {
    this.cachedLocation = null;
  }
}

export const ipGeoLocationService = IpGeoLocationService.shared;
